import React, { useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Paper
} from '@mui/material';
import {
  Dashboard as OverviewIcon,
  Person as StudentIcon,
  School as TeacherIcon,
  Sync as SyncIcon,
  Assessment as ReportIcon,
  CalendarMonth as MonthlyIcon,
  Public as ZoneIcon,
  Psychology as LevelIcon,
  Storage as DataWarehouseIcon,
  BugReport as DebugIcon
} from '@mui/icons-material';

import AttendanceOverview from '../../components/Attendance/AttendanceOverview';
import StudentAttendanceDetails from '../../components/Attendance/StudentAttendanceDetails';
import TeacherAttendanceDetails from '../../components/Attendance/TeacherAttendanceDetails';
import AttendanceSyncManager from '../../components/Attendance/AttendanceSyncManager';
import SchoolAttendanceReport from '../../components/Attendance/SchoolAttendanceReport';
import MonthlyAttendanceReport from '../../components/Attendance/MonthlyAttendanceReport';
import ZoneDashboard from '../../components/Attendance/ZoneDashboard';
import SchoolLevelAnalysis from '../../components/Attendance/SchoolLevelAnalysis';
import DataWarehouseStatus from '../../components/Attendance/DataWarehouseStatus';
import AttendanceDebugView from '../../components/Attendance/AttendanceDebugView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attendance-tabpanel-${index}`}
      aria-labelledby={`attendance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `attendance-tab-${index}`,
    'aria-controls': `attendance-tabpanel-${index}`,
  };
}

const Attendance: React.FC = () => {
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Attendance Management
      </Typography>
      
      <Typography variant="body1" color="textSecondary" gutterBottom>
        Monitor and analyze student and teacher attendance across all schools
      </Typography>

      <Paper sx={{ width: '100%', mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="attendance tabs" variant="scrollable" scrollButtons="auto">
            <Tab 
              icon={<OverviewIcon />}
              label="Overview" 
              {...a11yProps(0)} 
            />
            <Tab 
              icon={<StudentIcon />}
              label="Student Attendance" 
              {...a11yProps(1)} 
            />
            <Tab 
              icon={<TeacherIcon />}
              label="Teacher Attendance" 
              {...a11yProps(2)} 
            />
            <Tab 
              icon={<ReportIcon />}
              label="School Reports" 
              {...a11yProps(3)} 
            />
            <Tab 
              icon={<MonthlyIcon />}
              label="Monthly Report" 
              {...a11yProps(4)} 
            />
            <Tab 
              icon={<ZoneIcon />}
              label="Zone Analytics" 
              {...a11yProps(5)} 
            />
            <Tab 
              icon={<LevelIcon />}
              label="School Levels" 
              {...a11yProps(6)} 
            />
            <Tab 
              icon={<DataWarehouseIcon />}
              label="Data Warehouse" 
              {...a11yProps(7)} 
            />
            <Tab 
              icon={<DebugIcon />}
              label="Debug View" 
              {...a11yProps(8)} 
            />
            <Tab 
              icon={<SyncIcon />}
              label="Sync Management" 
              {...a11yProps(9)} 
            />
          </Tabs>
        </Box>
        
        <TabPanel value={value} index={0}>
          <AttendanceOverview />
        </TabPanel>
        
        <TabPanel value={value} index={1}>
          <StudentAttendanceDetails />
        </TabPanel>
        
        <TabPanel value={value} index={2}>
          <TeacherAttendanceDetails />
        </TabPanel>
        
        <TabPanel value={value} index={3}>
          <SchoolAttendanceReport />
        </TabPanel>
        
        <TabPanel value={value} index={4}>
          <MonthlyAttendanceReport />
        </TabPanel>
        
        <TabPanel value={value} index={5}>
          <ZoneDashboard />
        </TabPanel>
        
        <TabPanel value={value} index={6}>
          <SchoolLevelAnalysis />
        </TabPanel>
        
        <TabPanel value={value} index={7}>
          <DataWarehouseStatus />
        </TabPanel>
        
        <TabPanel value={value} index={8}>
          <AttendanceDebugView />
        </TabPanel>
        
        <TabPanel value={value} index={9}>
          <AttendanceSyncManager />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Attendance;